/**
 * Authentication Context and Provider
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { User } from '../api/types';
import { decodeJWT } from '@/utils/jwt';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (provider: string, isNewUser?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const isDemo = localStorage.getItem('isDemo');
    const hasCookie = !!Cookies.get('auth_token');
    console.log('[AuthContext] Check auth called: isDemo=' + isDemo + ', hasCookie=' + hasCookie);

    try {
      const token = Cookies.get('auth_token');
      const isDemo = localStorage.getItem('isDemo') === 'true';

      // Skip auth check for demo users - handled by UniversalProtectedRoute
      if (isDemo) {
        console.log('[AuthContext] Skipping auth check for demo user');
        setIsLoading(false);
        return;
      }

      if (token) {
        // Decode JWT to check user type (for non-httpOnly tokens)
        const decoded = decodeJWT(token);

        // Regular restaurant owner flow - use /restaurants/profile
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://dineai.ai/api'}/restaurants/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const profile = await response.json();

            // Get user information from JWT
            let userId = 'unknown';
            let userEmail = 'unknown@example.com';
            let userName = 'Unknown User';

            if (decoded) {
              userId = decoded.user_id?.toString() || 'unknown';
              userEmail = decoded.email || 'unknown@example.com';
              userName = decoded.first_name ?
                `${decoded.first_name} ${decoded.last_name || ''}`.trim() :
                decoded.email || 'User';
            }

            // Set user from JWT and profile data
            setUser({
              id: userId,
              email: userEmail,
              name: userName,
              restaurantId: profile.restaurant_id,
              restaurantName: profile.restaurant_name,
              subscriptionPlan: profile.subscription_plan,
              isStaff: false,
            });
            return;
          }
        } catch (error) {
          console.error('Profile fetch failed:', error);
        }

        // No fallback - if token is invalid, clear it
        throw new Error('Invalid token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      Cookies.remove('auth_token');
      Cookies.remove('refresh_token');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (provider: string, isNewUser: boolean = true) => {
    try {
      setIsLoading(true);

      // Clear any previous plan selection data for fresh flow
      localStorage.removeItem('selected_plan');
      localStorage.removeItem('auth_provider');

      // Clear any demo mode flags when starting real login
      localStorage.removeItem('isDemo');
      localStorage.removeItem('demoPlan');
      localStorage.removeItem('demoAuthToken');
      
      // Real OAuth flow - redirect to OAuth provider
      const platform = 'web'; // We're always on web for restaurant login
      const authUrl = `${import.meta.env.VITE_API_URL || 'https://dineai.ai/api'}/v1/auth/restaurant/${provider}/login?platform=${platform}`;
      
      // Store the provider for when we come back
      localStorage.setItem('auth_provider', provider);
      
      // Redirect to OAuth provider
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePostLoginNavigation = async (token: string, provider: string, userName: string) => {
    // Check if user already has a restaurant by making API call
    try {
      const profileResponse = await fetch(`${import.meta.env.VITE_API_URL || 'https://dineai.ai/api'}/restaurants/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (profileResponse.ok) {
        const profile = await profileResponse.json();
        console.log('🔍 Profile API Response:', profile);
        
        // If user has a restaurant and has completed onboarding, go to dashboard
        if (profile.restaurant_id && profile.onboarding_completed_at) {
          console.log('✅ User has completed onboarding, redirecting to dashboard');
          navigate('/restaurant-dashboard');
          return;
        }
        // If user has a restaurant but hasn't completed onboarding
        else if (profile.restaurant_id && !profile.onboarding_completed_at) {
          console.log('⚠️ User has restaurant but not completed onboarding');
          // Check if they have any subscription (including trial)
          if (profile.subscription_plan) {
            // They have a plan (including trial), go to onboarding
            console.log('📋 User has plan, going to onboarding');
            navigate('/restaurant-setup');
          } else {
            // They created an account but didn't select a plan
            console.log('🎯 User needs to select a plan');
            navigate(`/plan-selection?provider=${provider}&name=${encodeURIComponent(userName)}`);
          }
          return;
        }
      }
    } catch (error) {
      console.error('Error checking user profile:', error);
    }
    
    // For new users or if profile check fails, check if they already selected a plan
    const selectedPlan = localStorage.getItem('selected_plan');
    if (selectedPlan && selectedPlan !== 'trial') {
      // They already selected a plan from pricing page, go directly to onboarding
      console.log('🎯 User pre-selected plan:', selectedPlan);
      navigate('/restaurant-setup');
    } else {
      // No pre-selected plan, go to plan selection
      navigate(`/plan-selection?provider=${provider}&name=${encodeURIComponent(userName)}`);
    }
  };

  const logout = async () => {
    try {
      // Clear tokens and user
      Cookies.remove('auth_token');
      Cookies.remove('refresh_token');

      // Clear VH Staff localStorage data
      localStorage.removeItem('user_type');
      localStorage.removeItem('vh_staff_data');

      setUser(null);
      navigate('/restaurant-login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const refreshUser = async () => {
    try {
      // Re-run auth check to refresh user from API
      await checkAuth();
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};